import { If } from "components";
import {
  isEmptyValue,
  removeElementInArrayByIndex,
  replaceElementInArray
} from "helpers/methods";
import differenceBy from "lodash/differenceBy";
import every from "lodash/every";
import filter from "lodash/filter";
import find from "lodash/find";
import findIndex from "lodash/findIndex";
import isEmpty from "lodash/isEmpty";
import { ChangeEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Error, Input, SelectableList } from "ui";

import { AttributeValue as IAttributeValue } from "../../categories.types";
import { AttributeFieldWrapper } from "../AttributeFieldWrapper";
import { AttributeValue } from "../AttributeValue";
import { AttributeFieldProps } from "./AttributeField.types";

import styles from "./AttributeField.module.scss";

const AttributeField = ({
  variant,
  placeholder,
  value,
  options = [],
  errorMessage,
  disabledMainFilter,
  disabledInput = false,
  onChange,
  onDelete
}: AttributeFieldProps) => {
  const { t } = useTranslation();
  const isPrimaryVariant = variant === "primary";
  const isDefaultVariant = variant === "default";

  const { values: attributeValues } = value;
  const { values: errorAttributeValues = null } = errorMessage ?? {};

  const customAttributeValues = useMemo(
    () => differenceBy(attributeValues, options, "value"),
    [attributeValues, options]
  );

  const onChangeAttributeValues = (newValues: IAttributeValue[]) => {
    onChange({
      ...value,
      values: newValues
    });
  };

  const [attributeValue, setAttributeValue] = useState("");
  const [editAttributeValue, setEditAttributeValue] =
    useState<IAttributeValue | null>(null);

  const onChangeAttributeValue = (e: ChangeEvent<HTMLInputElement>) => {
    setAttributeValue(e.target.value);
  };

  const disabledModifyAttributeValueButton = () => {
    return (
      isEmptyValue(attributeValue) ||
      (!!find(
        [
          ...options,
          ...filter(
            customAttributeValues,
            (customAttributeValue) => !customAttributeValue.delete
          )
        ],
        {
          value: attributeValue
        }
      ) &&
        editAttributeValue?.value !== attributeValue) ||
      disabledInput
    );
  };

  const onAddAttributeValue = () => {
    onChangeAttributeValues([...attributeValues, { value: attributeValue }]);
    setAttributeValue("");
  };

  const onEditAttributeValue = (option: IAttributeValue) => {
    return () => {
      setAttributeValue(option.value);
      setEditAttributeValue(option);
    };
  };

  const onSaveEditedAttributeValue = () => {
    const editAttributeValueIndex = findIndex(attributeValues, {
      value: editAttributeValue?.value
    });

    onChangeAttributeValues(
      replaceElementInArray<IAttributeValue>(
        attributeValues,
        { ...editAttributeValue, value: attributeValue },
        editAttributeValueIndex
      )
    );

    setAttributeValue("");
    setEditAttributeValue(null);
  };

  const onDeleteAttributeValue = (option: IAttributeValue) => {
    return () => {
      const editAttributeValueIndex = findIndex(attributeValues, {
        value: option.value
      });

      const modifiedAttributeValues = option.id
        ? replaceElementInArray<IAttributeValue>(
            attributeValues,
            {
              ...option,
              delete: !option.delete
            },
            editAttributeValueIndex
          )
        : removeElementInArrayByIndex<IAttributeValue>(
            attributeValues,
            editAttributeValueIndex
          );

      onChangeAttributeValues(modifiedAttributeValues);
    };
  };

  return (
    <AttributeFieldWrapper
      value={value}
      onChange={onChange}
      onDelete={onDelete}
      errorMessage={errorMessage}
      disabledMainFilter={disabledMainFilter}
      readOnly={!isPrimaryVariant}
    >
      <If condition={!isPrimaryVariant}>
        <SelectableList
          values={attributeValues}
          onChange={onChangeAttributeValues}
          options={options}
        />
      </If>

      <If condition={!isDefaultVariant}>
        <>
          <div className={styles.attribute__input}>
            <Input
              placeholder={placeholder}
              value={attributeValue}
              onChange={onChangeAttributeValue}
              maxLength={30}
              disabled={disabledInput}
            />
            <Button
              type="button"
              disabled={disabledModifyAttributeValueButton()}
              onClick={
                editAttributeValue
                  ? onSaveEditedAttributeValue
                  : onAddAttributeValue
              }
            >
              {editAttributeValue ? t("Save") : t("Add")}
            </Button>
          </div>

          <If
            condition={
              !isEmpty(customAttributeValues) &&
              !every(customAttributeValues, { delete: true })
            }
          >
            <div className={styles.attribute__values}>
              {customAttributeValues.map((attribute: IAttributeValue) => (
                <If key={attribute.value} condition={!attribute.delete}>
                  <AttributeValue
                    key={attribute.value}
                    title={attribute.value}
                    onEdit={onEditAttributeValue(attribute)}
                    onDelete={onDeleteAttributeValue(attribute)}
                    isDeleteDisabled={attribute.isValidToDelete === false}
                  />
                </If>
              ))}
            </div>
          </If>
        </>
      </If>

      <Error message={errorAttributeValues?.message} isMargin={false} />
    </AttributeFieldWrapper>
  );
};

export default AttributeField;
